import type { RefresherEventDetail } from '@ionic/core/components';
import {
  IonContent,
  IonPage,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRow,
} from '@ionic/react';
import { chevronDownCircleOutline } from 'ionicons/icons';
import { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import axios from '../../axios';
import Header from '../../components/Header';
import NoRecords from '../../components/NoRecords';
import { AppContext } from '../../providers/AppContext';

const SurveyShow: React.FC = () => {
  const isMounted = useRef(true);
  const { t } = useTranslation();
  const { state } = useContext(AppContext);

  const [data, setData] = useState<any>({ dataSet: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const getData = () => {
    isMounted.current = true;
    const url = `/resource/Survey?limit_page_length=${state.maxLength}&limit_start=${currentPage * 5 + 0}&fields=["name", "si_survey_code"]&order_by=creation%20desc`;

    const config: any = {
      method: 'get',
      url,
      headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
    };

    axios(config)
      .then((res: any) => {
        if (res?.data.data && res.data.data.length > 0) {
          const Data = { ...data };
          Data.dataSet.push(...res.data.data);
          if (isMounted.current) {
            setData(Data);
            setFilteredData(Data.dataSet);
            setDisableInfiniteScroll(res.data.data.length < state.maxLength);
          }
        } else {
          if (isMounted.current) setDisableInfiniteScroll(true);
        }
      })
      .catch((err) => console.log(err))
      .finally(() => {
        if (isMounted.current) {
          setIsLoading(false);
          setCurrentPage(currentPage + 1);
        }
      });
  };

  useEffect(() => {
    getData();
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredData(data.dataSet);
    } else {
      const q = searchText.toLowerCase();
      setFilteredData(data.dataSet.filter((item: any) =>
        item.name?.toLowerCase().includes(q) || item.si_survey_code?.toLowerCase().includes(q)
      ));
    }
  }, [searchText, data.dataSet]);

  function doRefresh(event: CustomEvent<RefresherEventDetail>) {
    getData();
    setTimeout(() => { event.detail.complete(); }, 2000);
  }

  async function searchNext($event: CustomEvent<void>) {
    await getData();
    ($event.target as HTMLIonInfiniteScrollElement).complete();
  }

  // Color-code survey cards by name hash
  const getAccentColor = (name: string) => {
    const palette = ['#1d508d', '#6247aa', '#0ea5e9', '#0d9488', '#d97706', '#e53935'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  };

  const getInitials = (name: string) => {
    const parts = name?.split('-') || [];
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name?.slice(0, 2).toUpperCase() || 'SV';
  };

  return (
    <IonPage>
      <Header enableBackButton='/survey-home' title={t('Survey.history')} />
      <IonContent style={{ '--background': '#f0f4f8' }}>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t('Utils.pullRefresh')}
            refreshingSpinner="circles"
            refreshingText={t('Utils.refreshing')}
          />
        </IonRefresher>

        {/* ── Hero / Search bar ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1d508d 0%, #2a6db5 100%)',
          padding: '16px 14px 14px',
        }}>
          <p style={{ margin: '0 0 10px', fontSize: '11px', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {t('Survey.history')}
          </p>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '8px 12px',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={`${t('Utils.filter')}...`}
              style={{
                background: 'transparent', border: 'none', outline: 'none',
                color: '#fff', fontSize: '14px', flex: 1,
              }}
            />
            {searchText && (
              <span onClick={() => setSearchText('')} style={{ color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</span>
            )}
          </div>
        </div>

        {/* ── Skeleton loaders ── */}
        {isLoading && [1, 2, 3, 4, 5, 6].map((el) => (
          <div key={el} style={{
            margin: '10px 12px', borderRadius: '16px', background: '#fff',
            padding: '14px 16px', boxShadow: '0 3px 14px rgba(0,0,0,0.06)',
            display: 'flex', gap: '12px', alignItems: 'center',
          }}>
            <IonSkeletonText animated style={{ width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0 } as any} />
            <div style={{ flex: 1 }}>
              <IonSkeletonText animated style={{ width: '55%', height: '13px', borderRadius: '6px', marginBottom: '8px' }} />
              <IonSkeletonText animated style={{ width: '35%', height: '10px', borderRadius: '6px' }} />
            </div>
            <IonSkeletonText animated style={{ width: '60px', height: '26px', borderRadius: '20px' }} />
          </div>
        ))}

        {/* ── Survey cards ── */}
        <div style={{ padding: '10px 12px 80px' }}>
          {filteredData.map((item: any, key: any) => {
            const color = getAccentColor(item.name);
            return (
              <div
                key={key}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 18px rgba(0,0,0,0.07)',
                  marginBottom: '10px',
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                }}
              >
                {/* Color-coded avatar */}
                <div style={{
                  width: '46px', height: '46px', borderRadius: '13px',
                  background: `${color}18`,
                  border: `1.5px solid ${color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 800, color }}>{getInitials(item.name)}</span>
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1a2a3a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.name}
                  </p>
                  {item.si_survey_code && (
                    <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#6b7b8f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t('Survey.code')}: {item.si_survey_code}
                    </p>
                  )}
                </div>

                {/* Details pill button */}
                <Link
                  to={{ pathname: '/view-survey', state: item.name }}
                  style={{ textDecoration: 'none', flexShrink: 0 }}
                >
                  <div style={{
                    background: 'linear-gradient(90deg, #1d508d, #2a6db5)',
                    color: '#fff',
                    borderRadius: '20px',
                    padding: '5px 13px',
                    fontSize: '11px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}>
                    {t('Assets.Details.Details')} →
                  </div>
                </Link>
              </div>
            );
          })}

          {/* Empty state */}
          {!isLoading && filteredData.length === 0 && (
            <IonRow>
              <NoRecords width="60%" smallText={false} />
            </IonRow>
          )}
        </div>

        <IonInfiniteScroll disabled={disableInfiniteScroll} onIonInfinite={(e: CustomEvent<void>) => searchNext(e)}>
          <IonInfiniteScrollContent loadingSpinner="crescent" loadingText={t('Utils.pleasewait')} />
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default SurveyShow;
